import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useMount } from 'utils/hooks';

import api, { APIError } from 'api';
import { getEnv } from 'utils/env';

import PageSpinner from 'components/PageSpinner';

import _SiteTheme from './SiteTheme';
import _ErrorBoundary from './ErrorBoundary';


const defaultState = {
    fetching: false,
    role: '',
    brand: {},
};

export const SET_SITE_ROLE = 'SET_SITE_ROLE';
export const SET_FETCHING_SITE_ROLE = 'SET_FETCHING_SITE_ROLE';

export const reducer = (state = defaultState, action = {}) => {
    switch (action.type) {
        case SET_FETCHING_SITE_ROLE:
            return defaultState;
        case SET_SITE_ROLE:
            return { 
                ...state,
                role: action.role,
                brand: action.brand,
                fetching: false,
            };
        default:
            return state;
    }
};

export const fetchSite = (callbacks = {}) => {
    return dispatch => {
        callbacks.start && callbacks.start();
        dispatch({ type: SET_FETCHING_SITE_ROLE });
        api.options('status/')
            .then(resp => {
                const brand_slug = resp.site_brand;
                if (brand_slug) {
                    import(`../../../brands/${resp.site_brand}/index.js`).then(module => {
                        const brand = module.default;
                        const role = resp.site_role;
                        dispatch({ type: SET_SITE_ROLE, role, brand });
                        callbacks.done && callbacks.done(resp);
                    });
                } else {
                    throw new APIError('Failed to identify site');
                }
            })
            .catch(err => {
                callbacks.fail && callbacks.fail(err);
            });
    };
};

const SiteError = ({ error }) => {
    useEffect(() => {
        throw error;
    });

    return null;
};

const SiteTheme = connect(
    ({ site_options }, ownProps) => ({ role: site_options.role }),
    undefined
)(_SiteTheme);

/* SiteOptions sets site theme (see styles/theme-*)
 * and passes 'role' to children
 * for rendering to site-specific routes
 */
export const SiteOptions = connect(
    ({ site_options, auth }, ownProps) => ({
        fetching: site_options.fetching,
        role: site_options.role,
        brand: site_options.brand,
        loggedIn: auth.loggedIn,
    }),
    (dispatch, ownProps) => ({
        fetchSite: callbacks => dispatch(fetchSite(callbacks)),
    })
)(function SiteOptions({ brand, children, fetching, role, fetchSite, loggedIn }) {
    const [error, setError] = useState(false);
    const mounted = useRef(true);
    useEffect(() => {
        !role && fetchSite({
            fail: err => mounted.current && setError(err),
        });
        return () => {mounted.current = false;};
    }, [fetchSite, role]);

    if (error) {
        return <SiteError error={error} />;
    }

    if (fetching) {
        return <PageSpinner />;
    }

    return <SiteTheme>{children({ brand, role, loggedIn })}</SiteTheme>;
});

export const ErrorBoundary = connect(
    ({user }, ownProps) => ({
        user
    }),
    (dispatch, ownProps) => ({})
)(_ErrorBoundary);

const LazyBrandComponent = ({ brand, componentName }) => {
    const [Component, setComponent] = useState(() => PageSpinner);

    useMount(() => {
        let mounted = true;
        import(`../../../brands/${brand.slug}/components/${componentName}.jsx`).then(module => {
            const BrandComponent = module.default;
            if (mounted) {
                setComponent(() => BrandComponent);
            }
        });
        return () => (mounted = false);
    });

    return <Component brand={brand} />;
};

const connectBrandComponent = componentName => {
    return connect(
        ({ site_options }, ownProps) => {
            const brand = site_options.brand;
            return {
                brand,
                BrandComponent: ({ brand }) => (
                    <LazyBrandComponent brand={brand} componentName={componentName} />
                ),
            };
        },
        null,
        (stateProps, dispatchProps, ownProps) => ({
            ...ownProps,
            ...stateProps,
            ...dispatchProps,
        }),
        {
            areMergedPropsEqual: (next, prev) => next.brand.slug === prev.brand.slug,
        }
    )(function ({ brand, BrandComponent }) {
        return <BrandComponent brand={brand} />;
    });
};

export const BrandHeader = connectBrandComponent('BrandHeader');
export const BrandFooter = connectBrandComponent('BrandFooter');
